import { capture } from '@parcel/analytics/browser';
import { SIGNAL_TYPE_DEFINITIONS, groupSubscribableSignalTypesByStage } from '@parcel/types';
import type { SignalPriority, SignalType } from '@parcel/types';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { Check, Info, MoreHorizontal, Plus } from 'lucide-react';
import * as React from 'react';
import {
  type SignalSubscription,
  type SubscriptionUpsertInput,
  useSignalSubscriptionMutations,
  useSignalSubscriptions,
} from '../../lib/signal-subscriptions.js';
import type { ApiSignalType } from '../../lib/signals.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { SettingsPageHeader, SettingsShell } from '../settings/settings-shell.js';
import { TableSkeletonRows } from '../settings/settings-skeletons.js';
import { PriorityDropdown } from './priority-dropdown.js';

function typeDefinition(signalType: ApiSignalType) {
  return SIGNAL_TYPE_DEFINITIONS.find((definition) => definition.value === signalType);
}

/** Pick a priority before a signal type is added. */
function NewSignalDialog({
  signalType,
  open,
  onOpenChange,
  onCreate,
}: {
  signalType: ApiSignalType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (priority: SignalPriority) => void;
}) {
  const [priority, setPriority] = React.useState<SignalPriority>('medium');

  // Reset the form each time a new type is chosen.
  React.useEffect(() => {
    if (signalType) setPriority('medium');
  }, [signalType]);

  if (!signalType) return null;
  const definition = typeDefinition(signalType);
  const label = definition?.label ?? signalType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add {label}</DialogTitle>
          <DialogDescription>{definition?.description}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-foreground">Priority</div>
            <p className="text-xs text-muted-foreground">
              A News feed row's priority, and project scoring.
            </p>
          </div>
          <PriorityDropdown value={priority} onChange={setPriority} />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onCreate(priority);
              onOpenChange(false);
            }}
          >
            Add signal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Confirmation before unsubscribing — removing a type changes the queue and scoring. */
function UnsubscribeDialog({
  subscription,
  open,
  onOpenChange,
  onConfirm,
}: {
  subscription: SignalSubscription | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  if (!subscription) return null;
  const label = typeDefinition(subscription.signalType)?.label ?? subscription.signalType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Unsubscribe from {label}?</DialogTitle>
          <DialogDescription>
            {label} signals will stop appearing in the News feed and its emails, and this signal
            type will no longer contribute to project scoring. You can re-add it at any time.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            Unsubscribe
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionRow({
  subscription,
  onUpsert,
  onRemove,
}: {
  subscription: SignalSubscription;
  onUpsert: (input: SubscriptionUpsertInput) => void;
  onRemove: () => void;
}) {
  const definition = typeDefinition(subscription.signalType);
  const label = definition?.label ?? subscription.signalType;

  return (
    // Not clickable as a whole, so no row hover; each control carries its own.
    <TableRow className="hover:bg-transparent">
      <TableCell className="py-2">
        <span className="flex items-center gap-1.5 font-medium text-foreground">
          {label}
          {/* The definition is one hover away rather than three lines under every row. */}
          {definition ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`About ${label}`}
                  className="rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {definition.description}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </span>
      </TableCell>

      <TableCell className="py-2">
        <PriorityDropdown
          variant="cell"
          value={subscription.priority}
          onChange={(priority) => onUpsert({ signalType: subscription.signalType, priority })}
        />
      </TableCell>

      <TableCell className="py-2 pr-2">
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label={`${label} options`}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onSelect={onRemove}
                className="text-destructive focus:text-destructive"
              >
                Unsubscribe
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Subscriptions under their project stage, in funnel order, so the table reads like the
 * lifecycle. A type no stage group knows about lands in Other rather than vanishing.
 */
function groupSubscriptionsByStage(subscriptions: SignalSubscription[]) {
  const groups = groupSubscribableSignalTypesByStage().map((group) => ({
    key: group.key,
    label: group.label,
    rows: subscriptions.filter((subscription) =>
      (group.signalTypes as readonly string[]).includes(subscription.signalType),
    ),
  }));
  const placed = new Set(groups.flatMap((group) => group.rows.map((row) => row.signalType)));
  const leftover = subscriptions.filter((subscription) => !placed.has(subscription.signalType));
  if (leftover.length > 0) {
    const other = groups.find((group) => group.key === 'other');
    if (other) other.rows.push(...leftover);
    else groups.push({ key: 'other', label: 'Other', rows: leftover });
  }
  return groups.filter((group) => group.rows.length > 0);
}

interface SignalSubscriptionListProps {
  subscriptions: SignalSubscription[];
  onUpsert: (input: SubscriptionUpsertInput) => void;
  onRemove: (signalType: SignalType) => void;
}

export function SignalSubscriptionList({
  subscriptions,
  onUpsert,
  onRemove,
}: SignalSubscriptionListProps) {
  const [addOpen, setAddOpen] = React.useState(false);
  const [draftType, setDraftType] = React.useState<ApiSignalType | null>(null);
  const [removeType, setRemoveType] = React.useState<ApiSignalType | null>(null);

  const subscribedTypes = React.useMemo(
    () => new Set(subscriptions.map((subscription) => subscription.signalType)),
    [subscriptions],
  );
  // Show the full subscribable catalog (reserved types like rfp are excluded by
  // the helper) grouped by stage in funnel order. Already-subscribed types
  // stay in the list, marked "Added" and non-selectable, so users can see the
  // whole catalog instead of wondering where a subscribed type went.
  const allGroups = React.useMemo(() => groupSubscribableSignalTypesByStage(), []);

  const removeSubscriptionTarget =
    subscriptions.find((subscription) => subscription.signalType === removeType) ?? null;

  return (
    <TooltipProvider>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {subscriptions.length} {subscriptions.length === 1 ? 'signal type' : 'signal types'}
        </p>
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button type="button" className="shrink-0 gap-2">
              <Plus className="h-4 w-4" aria-hidden />
              New signal
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0" align="end">
            <Command>
              <CommandInput placeholder="Search signal types…" />
              <CommandList>
                <CommandEmpty>No signal types found.</CommandEmpty>
                {allGroups.map((group) => (
                  <CommandGroup key={group.key} heading={group.label}>
                    {group.signalTypes.map((type) => {
                      const definition = typeDefinition(type);
                      if (!definition) return null;
                      const isSubscribed = subscribedTypes.has(type);
                      return (
                        <CommandItem
                          key={type}
                          value={`${definition.label} ${type}`}
                          disabled={isSubscribed}
                          onSelect={() => {
                            if (isSubscribed) return;
                            setAddOpen(false);
                            setDraftType(type);
                          }}
                          className={cn('gap-2', isSubscribed && 'opacity-60')}
                        >
                          <span
                            className={cn(
                              'min-w-0 flex-1 truncate font-medium',
                              isSubscribed && 'text-muted-foreground',
                            )}
                          >
                            {definition.label}
                          </span>
                          {isSubscribed ? (
                            <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                              <Check className="h-3.5 w-3.5" aria-hidden />
                              Added
                            </span>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="shrink-0 text-muted-foreground"
                                  aria-label={`${definition.label} definition`}
                                  // The info icon is a read affordance, not a row
                                  // action — stop the click/tap from reaching the
                                  // CommandItem and adding the subscription.
                                  onClick={(event) => event.stopPropagation()}
                                  onPointerDown={(event) => event.stopPropagation()}
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                {definition.description}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="overflow-hidden rounded-lg border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead>Signal</TableHead>
              <TableHead className="w-44">Priority</TableHead>
              <TableHead className="w-16">
                <span className="sr-only">Options</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="py-10 text-center text-sm text-muted-foreground">
                  No signal types yet. Add one to start tracking signals.
                </TableCell>
              </TableRow>
            ) : (
              groupSubscriptionsByStage(subscriptions).map((group) => (
                <React.Fragment key={group.key}>
                  <TableRow className="hover:bg-transparent" data-testid="signal-stage-group">
                    <TableCell
                      colSpan={3}
                      className="bg-muted/30 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      {group.label}
                    </TableCell>
                  </TableRow>
                  {group.rows.map((subscription) => (
                    <SubscriptionRow
                      key={subscription.signalType}
                      subscription={subscription}
                      onUpsert={onUpsert}
                      onRemove={() => setRemoveType(subscription.signalType)}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <NewSignalDialog
        signalType={draftType}
        open={draftType !== null}
        onOpenChange={(open) => {
          if (!open) setDraftType(null);
        }}
        onCreate={(priority) => {
          if (draftType) onUpsert({ signalType: draftType, priority });
        }}
      />

      <UnsubscribeDialog
        subscription={removeSubscriptionTarget}
        open={removeType !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveType(null);
        }}
        onConfirm={() => {
          if (removeType) onRemove(removeType);
        }}
      />
    </TooltipProvider>
  );
}

/** The list's shape before subscriptions land, so the empty state never flashes. */
function SignalSubscriptionListSkeleton() {
  return (
    <div data-testid="signal-subscriptions-skeleton">
      <div className="mb-4 flex items-center justify-between gap-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-32" />
      </div>
      <div className="overflow-hidden rounded-lg border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead>Signal</TableHead>
              <TableHead className="w-44">Priority</TableHead>
              <TableHead className="w-16">
                <span className="sr-only">Options</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableSkeletonRows columns={3} rows={6} />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function SignalsSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;

  const subscriptionsQuery = useSignalSubscriptions(workspaceId);
  const mutations = useSignalSubscriptionMutations(workspaceId);
  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="Signals"
        description="Choose which signals count in this workspace's News feed and their priority. Email is personal: Settings → Notifications."
      />
      {(Boolean(workspaceSlug) && isSupabaseConfigured && workspaceQuery.isPending) ||
      (Boolean(workspaceId) && subscriptionsQuery.isPending) ? (
        <SignalSubscriptionListSkeleton />
      ) : subscriptionsQuery.isError ? (
        <p className="text-sm text-muted-foreground">
          Couldn't load subscriptions.{' '}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => void subscriptionsQuery.refetch()}
          >
            Retry
          </button>
        </p>
      ) : (
        <SignalSubscriptionList
          subscriptions={subscriptionsQuery.data?.subscriptions ?? []}
          onUpsert={(input) => {
            if (input.priority) capture('signal_subscription_saved', { priority: input.priority });
            mutations.upsert.mutate(input);
          }}
          onRemove={(signalType) => mutations.remove.mutate(signalType)}
        />
      )}
    </SettingsShell>
  );
}
